import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { projectAPI, departmentAPI } from "../services/api";
import ProjectMap from "../components/project/ProjectMap";
import Pagination from "../components/common/Pagination";

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    keyword: "",
    fiscal_year: "",
    department: "",
    province: "",
  });

  const fiscalYears = ["2566", "2567", "2568"];
  const provinces = ["ศรีสะเกษ", "ยโสธร"];

  const hasActiveFilters = Object.values(filters).some(
    (value) => value && value.trim() !== "",
  );

  // ---------------- FETCH DEPARTMENTS ----------------
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        console.log("Fetching departments...");
        const response = await departmentAPI.getAll();
        console.log("Departments response:", response);
        console.log("Departments data:", response.data);

        if (response.data && response.data.success) {
          setDepartments(response.data.data);
        } else if (Array.isArray(response.data)) {
          setDepartments(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    fetchDepartments();
  }, []);
  // ---------------- FETCH PROJECTS ----------------
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
        };

        console.log("Fetching with params:", params);

        const response = await projectAPI.getAll(params);
        console.log("API Response:", response.data); // ดูจำนวนที่ได้จาก API

        setProjects(response.data.data);

        // อัปเดต pagination ด้วยข้อมูลจาก API
        setPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages,
        });
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [pagination.page, pagination.limit, filters]); // สำคัญ: filters ต้องอยู่ใน dependency

  // ---------------- HANDLE FILTER ----------------
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 })); // รีเซ็ตหน้าแรก
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      keyword: "",
      fiscal_year: "",
      department: "",
      province: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const getFilterLabel = (key, value) => {
    switch (key) {
      case "keyword":
        return `ค้นหา: ${value}`;
      case "fiscal_year":
        return `ปีงบประมาณ: ${value}`;
      case "department":
        const dept = departments.find((d) => d.dpt_id === value);
        return `หน่วยงาน: ${dept ? dept.dpt_name : value}`;
      case "province":
        return `จังหวัด: ${value}`;
      default:
        return value;
    }
  };

  const formatBudget = (budget) => {
    return new Intl.NumberFormat("th-TH", {
      style: "currency",
      currency: "THB",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(budget || 0);
  };

  return (
    <>
      <Helmet>
        <title>พื้นที่บริการวิชาการ | มหาวิทยาลัยราชภัฏศรีสะเกษ</title>
        <meta
          name="description"
          content="พื้นที่บริการวิชาการและพัฒนาท้องถิ่น มหาวิทยาลัยราชภัฏศรีสะเกษ"
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 py-12">
        <div className="container-custom">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-block p-4 bg-primary-100 rounded-full mb-4">
              <i className="fas fa-map text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4 relative inline-block pb-4 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-24 after:h-1 after:bg-secondary-500 after:rounded-full">
              พื้นที่บริการวิชาการ
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              โครงการบริการวิชาการเพื่อการพัฒนาท้องถิ่นอย่างยั่งยืน
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={() => setShowMap(false)}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                !showMap
                  ? "bg-primary-500 text-white shadow-lg"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <i className="fas fa-list mr-2"></i>
              รายการ
            </button>
            <button
              onClick={() => setShowMap(true)}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                showMap
                  ? "bg-primary-500 text-white shadow-lg"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <i className="fas fa-map-marked-alt mr-2"></i>
              แผนที่
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="ค้นหาชื่อโครงการ / ผู้รับผิดชอบ / พื้นที่..."
                value={filters.keyword}
                onChange={(e) => handleFilterChange("keyword", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />

              <select
                value={filters.fiscal_year}
                onChange={(e) =>
                  handleFilterChange("fiscal_year", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="">ปีงบประมาณทั้งหมด</option>
                {[...fiscalYears]
                  .sort((a, b) => Number(b) - Number(a))
                  .map((year) => (
                    <option key={year} value={year}>
                      ปี {year}
                    </option>
                  ))}
              </select>

              <select
                value={filters.department}
                onChange={(e) =>
                  handleFilterChange("department", e.target.value)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="">หน่วยงานทั้งหมด</option>
                {departments.map((dept) => (
                  <option key={dept.dpt_id} value={dept.dpt_id}>
                    {dept.dpt_name}
                  </option>
                ))}
              </select>

              <select
                value={filters.province}
                onChange={(e) => handleFilterChange("province", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="">จังหวัดทั้งหมด</option>
                {provinces.map((prov) => (
                  <option key={prov} value={prov}>
                    {prov}
                  </option>
                ))}
              </select>
            </div>

            {/* Active Filter Tags */}
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                <span className="text-sm text-gray-600">ตัวกรองที่ใช้งาน:</span>
                {Object.entries(filters).map(([key, value]) =>
                  value ? (
                    <div
                      key={key}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                    >
                      <span>{getFilterLabel(key, value)}</span>
                      <button
                        onClick={() => handleFilterChange(key, "")}
                        className="hover:text-primary-900"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : null,
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-red-500 hover:text-red-700 transition-colors ml-2"
                >
                  <i className="fas fa-times-circle mr-1"></i>
                  ล้างตัวกรอง
                </button>
              </div>
            )}
          </div>

          {/* Results Info */}
          {!loading && !showMap && (
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                พบทั้งหมด{" "}
                <span className="font-semibold text-primary-500">
                  {pagination.total}
                </span>{" "}
                โครงการ
                {hasActiveFilters && (
                  <span className="ml-2 text-sm text-gray-500">
                    (จากตัวกรองที่เลือก)
                  </span>
                )}
              </p>
              <select
                value={pagination.limit}
                onChange={handleLimitChange}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="9">9 รายการ/หน้า</option>
                <option value="18">18 รายการ/หน้า</option>
                <option value="27">27 รายการ/หน้า</option>
              </select>
            </div>
          )}

          {/* Content */}
          {showMap ? (
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <ProjectMap
                projects={projects}
                loading={loading}
                currentProvince={filters.province}
                onProvinceChange={(province) =>
                  handleFilterChange("province", province)
                }
              />
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="spinner"></div>
            </div>
          ) : projects.length > 0 ? (
            <>
              {/* Projects Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <div
                    key={project.project_id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1"
                  >
                    {/* Header with color border */}
                    <div
                      className="h-2"
                      style={{ backgroundColor: project.dpt_color }}
                    ></div>

                    <div className="p-6">
                      {/* Title */}
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 line-clamp-2 h-14">
                        <Link
                          to={`/projects/${project.project_id}`}
                          className="hover:text-primary-500 transition-colors"
                        >
                          {project.project_name}
                        </Link>
                      </h3>

                      {/* Department */}
                      <p
                        className="text-sm font-medium mb-2"
                        style={{ color: project.dpt_color }}
                      >
                        <i className="fas fa-building mr-1"></i>
                        {project.dpt_name}
                      </p>

                      {/* Project Leader */}
                      <p className="text-sm text-gray-600 mb-2">
                        <i className="fas fa-user text-primary-500 mr-1"></i>
                        {project.mem_fname} {project.mem_sname}
                      </p>

                      {/* Location */}
                      <p className="text-sm text-gray-600 mb-2">
                        <i className="fas fa-map-marker-alt text-primary-500 mr-1"></i>
                        {project.subdistrict} {project.district}{" "}
                        {project.province}
                      </p>

                      {/* Fiscal Year */}
                      <p className="text-xs text-gray-500 mb-4">
                        <i className="fas fa-calendar mr-1"></i>
                        ปีงบประมาณ {project.fiscal_year}
                      </p>

                      {/* View Detail Button */}
                      <Link
                        to={`/projects/${project.project_id}`}
                        className="inline-flex items-center justify-center w-full gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                      >
                        ดูรายละเอียด
                        <i className="fas fa-arrow-right"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    onPageChange={(page) =>
                      setPagination((prev) => ({ ...prev, page }))
                    }
                  />
                </div>
              )}
            </>
          ) : (
            // No Results
            <div className="bg-white max-w-md mx-auto text-center py-16">
              <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="fas fa-map-marked-alt text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                ไม่พบโครงการ
              </h3>
              <p className="text-gray-600 mb-8">
                ไม่พบโครงการที่ตรงกับเงื่อนไขที่เลือก
              </p>
              <button
                onClick={clearFilters}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <i className="fas fa-redo-alt mr-2"></i>
                ล้างตัวกรองทั้งหมด
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectsPage;
